import Link from 'next/link'
import numeral from 'numeral'
import { useMemo, useState } from 'react'

import { useUser } from 'src/providers/UserProvider'

import { ButtonRebrand } from 'src/components/atoms/ButtonRebrand'
import { PillSwitchRebrand } from 'src/components/atoms/PillSwitchRebrand'
import { TypographyRebrand } from 'src/components/atoms/TypographyRebrand'
import { SliderInputCardRebrand } from 'src/components/molecules/SliderInputCardRebrand'
import {
  calculateCarbon,
  calculateFromTrees,
  PRICE_PER_TREE,
  withDefaults
} from 'src/components/organisms/CarbonCalculator/helper'

import {
  CalculatorSectionRebrand as CalculatorSectionRebrandSchema,
  InternalLink
} from 'src/config/generated/sanitySchema'
import { carbonCalculatorPath, donatePath } from 'src/config/paths'
import { trackEvent } from 'src/utils/ga'

type PackageLinkData = {
  label?: string
  internalLink?: InternalLink
}

type BudgetTab = 'budget' | 'trees'

export interface CalculatorSectionRebrandProps
  extends Omit<CalculatorSectionRebrandSchema, '_type' | 'packageLink'> {
  packageLink?: PackageLinkData
}

const formatNumber = (n: number) => numeral(n).format('0,0')

const PackageLinkSentence = ({ link }: { link?: PackageLinkData }) => {
  if (!link?.label || !link?.internalLink?.path) return null
  return (
    <p className='font-body-rebrand text-[16px] leading-[1.6] text-limeFresh'>
      Thinking bigger? Our{' '}
      <Link
        href={link.internalLink.path}
        className='underline decoration-solid underline-offset-2 hover:opacity-80'
      >
        {link.label}
      </Link>{' '}
      might be a better fit.
    </p>
  )
}

type ResultPanelProps = {
  primaryValue: string
  primaryLabel: string
  caption: React.ReactNode
  donateQuantity: number
  packageLink?: PackageLinkData
  showDonateButton?: boolean
  onDonate?: () => void
}

const ResultPanel = ({
  primaryValue,
  primaryLabel,
  caption,
  donateQuantity,
  packageLink,
  showDonateButton = true,
  onDonate
}: ResultPanelProps) => {
  const donateUrl = donatePath({
    queryParams: { quantity: donateQuantity }
  })

  return (
    <div
      className={`flex flex-1 flex-col gap-[15px] bg-forestDeep p-8 lg:p-12 text-limeFresh${
        showDonateButton ? '' : ' lg:self-center'
      }`}
    >
      <div className='flex flex-col gap-2'>
        <div className='flex flex-wrap items-baseline gap-[5px]'>
          <TypographyRebrand variant='h2'>
            <strong>{primaryValue}</strong>
          </TypographyRebrand>
          <TypographyRebrand variant='h5'>{primaryLabel}</TypographyRebrand>
        </div>
        <p className='font-body-rebrand text-[16px] leading-[1.6]'>{caption}</p>
      </div>
      {showDonateButton && (
        <Link href={donateUrl} className='self-start' onClick={onDonate}>
          <ButtonRebrand variant='lime'>Donate trees</ButtonRebrand>
        </Link>
      )}
      <PackageLinkSentence link={packageLink} />
    </div>
  )
}

const CARBON_MIN = 10
const CARBON_MAX = 2500
const CARBON_STEP = 10
const CARBON_DEFAULT = 10

export const CarbonVariant = ({
  heading,
  description,
  packageLink,
  showDonateButton = true,
  defaultEmissions = CARBON_DEFAULT,
  step = CARBON_STEP,
  min = CARBON_MIN
}: {
  heading?: string
  description?: React.ReactNode
  packageLink?: PackageLinkData
  showDonateButton?: boolean
  defaultEmissions?: number
  step?: number
  min?: number
}) => {
  const { user } = useUser()
  const [emissions, setEmissions] = useState(defaultEmissions)

  const numberOfTrees = useMemo(() => {
    if (emissions === 0) return 0
    const res = calculateCarbon(withDefaults({ emissions, stems: 1000 }))
    return Math.ceil(res.stems)
  }, [emissions])

  return (
    <div className='flex w-full flex-col gap-5'>
      {heading && (
        <TypographyRebrand variant='h4' color='forestDeep'>
          {heading}
        </TypographyRebrand>
      )}
      {description && (
        <TypographyRebrand variant='body'>{description}</TypographyRebrand>
      )}
      <div className='flex w-full flex-col items-stretch gap-6 lg:flex-row'>
        <SliderInputCardRebrand
          label='Yearly tCO₂e output'
          value={emissions}
          unitSuffix='tCO₂e'
          min={min}
          max={CARBON_MAX}
          step={step}
          onChange={setEmissions}
          onInputBlur={val =>
            trackEvent('carbon_calculator_input', {
              account_type: user?.Account?.Type,
              business_type: user?.Account?.BusinessType,
              business_size: user?.Account?.BusinessSize,
              value: val
            })
          }
          onSliderRelease={val =>
            trackEvent('carbon_calculator_slider', {
              account_type: user?.Account?.Type,
              business_type: user?.Account?.BusinessType,
              business_size: user?.Account?.BusinessSize,
              value: val
            })
          }
          minLabel={`${min} tCO₂e`}
          maxLabel={`${CARBON_MAX} tCO₂e`}
          helpLinkLabel='How we calculate these'
          helpLinkHref={carbonCalculatorPath}
        />
        <ResultPanel
          primaryValue={formatNumber(numberOfTrees)}
          primaryLabel='Native trees'
          caption={
            <>
              Have the potential to remove{' '}
              <span className='font-body-rebrand font-bold'>
                {formatNumber(emissions)} tCO₂
              </span>{' '}
              over 50 years of continuous growth.
            </>
          }
          donateQuantity={numberOfTrees}
          packageLink={packageLink}
          showDonateButton={showDonateButton}
          onDonate={() =>
            trackEvent('carbon_calculator_button', {
              account_type: user?.Account?.Type,
              business_type: user?.Account?.BusinessType,
              business_size: user?.Account?.BusinessSize,
              value: numberOfTrees
            })
          }
        />
      </div>
    </div>
  )
}

const TABS: readonly { label: string; value: BudgetTab }[] = [
  { label: 'Budget', value: 'budget' },
  { label: 'Trees', value: 'trees' }
]

const TREES_MIN = 1
const TREES_MAX = 2500
const TREES_STEP = 1
const TREES_DEFAULT = 46

const BUDGET_MIN = TREES_MIN * PRICE_PER_TREE
const BUDGET_MAX = TREES_MAX * PRICE_PER_TREE
const BUDGET_STEP = PRICE_PER_TREE

export const BudgetVariant = ({
  heading,
  description,
  packageLink,
  showDonateButton = true,
  activeTab: controlledTab,
  onTabChange,
  defaultTrees = TREES_DEFAULT
}: {
  heading: string
  description?: React.ReactNode
  packageLink?: PackageLinkData
  showDonateButton?: boolean
  activeTab?: BudgetTab
  onTabChange?: (tab: BudgetTab) => void
  defaultTrees?: number
}) => {
  const { user } = useUser()
  // Controlled when the parent passes `activeTab`, otherwise self-managed.
  const [internalTab, setInternalTab] = useState<BudgetTab>('budget')
  const activeTab = controlledTab ?? internalTab
  // Each tab has its own free-form value (budget can be any dollar amount; trees
  // is a whole count). Switching tabs syncs one from the other so they stay
  // equivalent, while typing within a tab isn't quantized.
  const [trees, setTrees] = useState(defaultTrees)
  const [budget, setBudget] = useState(defaultTrees * PRICE_PER_TREE)

  const treeCount =
    activeTab === 'trees' ? trees : Math.floor(budget / PRICE_PER_TREE)
  const dollarTotal = activeTab === 'trees' ? trees * PRICE_PER_TREE : budget
  const carbon = useMemo(() => calculateFromTrees(treeCount), [treeCount])

  const handleTabChange = (next: BudgetTab) => {
    // Carry the current value across so the other tab shows the equivalent.
    if (next === 'trees') setTrees(Math.floor(budget / PRICE_PER_TREE))
    else setBudget(trees * PRICE_PER_TREE)
    if (controlledTab === undefined) setInternalTab(next)
    onTabChange?.(next)
    trackEvent('funding_calculator_tab', {
      account_type: user?.Account?.Type,
      business_type: user?.Account?.BusinessType,
      business_size: user?.Account?.BusinessSize,
      value: next
    })
  }

  return (
    <div className='flex w-full flex-col gap-5'>
      <div className='flex w-full flex-col gap-5'>
        <TypographyRebrand variant='h4' color='forestDeep'>
          {heading}
        </TypographyRebrand>
        {description && (
          <TypographyRebrand variant='body'>{description}</TypographyRebrand>
        )}
        <PillSwitchRebrand
          className='self-start'
          options={TABS}
          value={activeTab}
          onChange={next => handleTabChange(next as BudgetTab)}
          ariaLabel='Calculator input mode'
        />
      </div>
      <div className='flex w-full flex-col items-stretch gap-6 lg:flex-row'>
        {activeTab === 'trees' ? (
          <SliderInputCardRebrand
            label='Number of trees'
            value={trees}
            min={TREES_MIN}
            max={TREES_MAX}
            step={TREES_STEP}
            onChange={setTrees}
            onInputBlur={val =>
              trackEvent('trees_calculator_input', {
                account_type: user?.Account?.Type,
                business_type: user?.Account?.BusinessType,
                business_size: user?.Account?.BusinessSize,
                value: val
              })
            }
            onSliderRelease={val =>
              trackEvent('trees_calculator_slider', {
                account_type: user?.Account?.Type,
                business_type: user?.Account?.BusinessType,
                business_size: user?.Account?.BusinessSize,
                value: val
              })
            }
            minLabel='1 tree'
            maxLabel='2500 trees'
            helpLinkLabel='How we calculate impact'
            helpLinkHref={carbonCalculatorPath}
          />
        ) : (
          <SliderInputCardRebrand
            label='Your budget'
            value={budget}
            unitPrefix='$'
            min={BUDGET_MIN}
            max={BUDGET_MAX}
            step={BUDGET_STEP}
            onChange={setBudget}
            onInputBlur={val =>
              trackEvent('budget_calculator_input', {
                account_type: user?.Account?.Type,
                business_type: user?.Account?.BusinessType,
                business_size: user?.Account?.BusinessSize,
                value: val
              })
            }
            onSliderRelease={val =>
              trackEvent('budget_calculator_slider', {
                account_type: user?.Account?.Type,
                business_type: user?.Account?.BusinessType,
                business_size: user?.Account?.BusinessSize,
                value: val
              })
            }
            minLabel={numeral(BUDGET_MIN).format('$0,0')}
            maxLabel={numeral(BUDGET_MAX).format('$0,0')}
            helpLinkLabel='How we calculate impact'
            helpLinkHref={carbonCalculatorPath}
          />
        )}
        <ResultPanel
          primaryValue={
            activeTab === 'trees'
              ? numeral(dollarTotal).format('$0,0')
              : formatNumber(treeCount)
          }
          primaryLabel={activeTab === 'trees' ? 'total' : 'Native trees'}
          caption={
            activeTab === 'budget' ? (
              <>
                With a cost of{' '}
                <span className='font-body-rebrand font-bold'>
                  {numeral(dollarTotal).format('$0,0')}
                </span>
                , these trees would remove{' '}
                <span className='font-body-rebrand font-bold'>
                  {carbon.formattedCarbon} tCO₂e
                </span>{' '}
                over the next 50 years.
              </>
            ) : (
              <>
                By funding {formatNumber(treeCount)} trees, these trees would
                remove{' '}
                <span className='font-body-rebrand font-bold'>
                  {carbon.formattedCarbon} tCO₂e
                </span>{' '}
                over the next 50 years.
              </>
            )
          }
          donateQuantity={treeCount}
          packageLink={packageLink}
          showDonateButton={showDonateButton}
          onDonate={() =>
            trackEvent(
              activeTab === 'trees'
                ? 'trees_calculator_button'
                : 'budget_calculator_button',
              {
                account_type: user?.Account?.Type,
                business_type: user?.Account?.BusinessType,
                business_size: user?.Account?.BusinessSize,
                value: treeCount
              }
            )
          }
        />
      </div>
    </div>
  )
}

export const CalculatorSectionRebrand = ({
  heading,
  variant,
  packageLink,
  showDonateButton
}: CalculatorSectionRebrandProps) => {
  // Undefined (legacy content saved before this field existed) still shows the button.
  const showDonate = showDonateButton !== false
  return (
    <section className='bg-linen'>
      <div className='container mx-auto px-4 py-12 lg:px-20 lg:py-12'>
        {variant === 'budget' ? (
          <BudgetVariant
            heading={heading}
            packageLink={packageLink}
            showDonateButton={showDonate}
          />
        ) : (
          <CarbonVariant
            heading={heading}
            packageLink={packageLink}
            showDonateButton={showDonate}
          />
        )}
      </div>
    </section>
  )
}

export default CalculatorSectionRebrand
